import React, { useState, useRef, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { dashboardActions, recruiterActions } from "../redux/actions";
import ActionsDataTable from "../shared/DataTable/ActionsDataTable";

// ---------- helpers ----------
const getTrendClass = (pct) =>
  pct > 0 ? "text-success" : pct < 0 ? "text-danger" : "text-muted";

const getTrendIcon = (pct) =>
  pct > 0 ? "ri-arrow-right-up-line" : pct < 0 ? "ri-arrow-right-down-line" : "ri-subtract-line";

const fmtPct = (pct) => {
  if (pct === undefined || pct === null || Number.isNaN(pct)) return "0.00";
  const num = Number(pct);
  return `${num > 0 ? "+" : ""}${num.toFixed(2)}`;
};

const safeStat = (stats, i) => (stats && stats[i] ? stats[i] : { total: 0, percentage: 0 });

// ---------- empty state when no chart data ----------
const EmptyChart = ({ onAction, title = "No data yet" }) => (
  <div
    style={{
      height: 350,
      border: "1px dashed #e5e7eb",
      borderRadius: 12,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background:
        "radial-gradient(1200px 300px at 50% 0%, rgba(99,102,241,0.06), transparent 60%)",
    }}
  >
    <div style={{ textAlign: "center" }}>
      <div
        style={{
          width: 72,
          height: 72,
          margin: "0 auto 10px",
          borderRadius: "50%",
          background: "rgba(99,102,241,.12)",
          display: "grid",
          placeItems: "center",
        }}
      >
        <i className="ri-bar-chart-2-line" style={{ fontSize: 28, color: "var(--oxd-primary-gradient-start-color, #ff8226)" }} />
      </div>
      <h6 className="mb-1" style={{ color: "#111827" }}>{title}</h6>
      <p className="mb-3" style={{ color: "#6B7280" }}>
        Try selecting a different recruiter or date range.
      </p>
      {onAction && (
        <button className="btn btn-sm btn-outline-primary" onClick={onAction}>
          Change Recruiter
        </button>
      )}
    </div>
  </div>
);

const Dashboard = () => {
  // ---------- table state ----------
  const initialGridState = {
    length: 10,
    pageNumber: 1,
    orderBy: "",
    filterQuery: null,
    search: null,
  };

  const dispatch = useDispatch();
  const sortingInProgressRef = useRef(false);
  const [gridState, setGridState] = useState(initialGridState);
  const [defaultPage, setDefaultPage] = useState(initialGridState.pageNumber);

  // ---------- redux data ----------
  const stats = useSelector((s) => s.dashboardReducer.data);
  const recruiterStats = useSelector((s) => s.dashboardReducer.recruiterStats);
  const recruiterStatsLoading = useSelector((s) => s.dashboardReducer.recruiterStatsLoading);
  const recruiterList = useSelector((s) => s.recruiterReducer.allData);

  // ---------- table columns ----------
  const columns = [
    {
      name: "Sr.No",
      selector: (row, index) => index + 1,
      sortable: false,
      width: "100px",
    },
    { id: "name", name: "Recruiter Name", selector: (r) => r.name, sortable: true, grow: 1.5 },
    { id: "email", name: "Recruiter Email", selector: (r) => r.email, sortable: true, grow: 2 },
    { id: "country", name: "Country", selector: (r) => r.country, sortable: true, width: "140px" },
    { id: "state", name: "State", selector: (r) => r.state, sortable: true, width: "130px" },
    { id: "city", name: "City", selector: (r) => r.city, sortable: true, width: "140px" },
    {
      id: "contactNumber",
      name: "Contact Number",
      selector: (r) => r.contactNumber,
      sortable: true,
      width: "170px",
      cell: (row) => (
        <div className="d-flex align-items-center">
          <span>{row.contactNumber}</span>
        </div>
      ),
    },
    { id: "gender", name: "Gender", selector: (r) => r.gender, sortable: true, width: "120px" },
    { id: "designation", name: "Designation", selector: (r) => r.designation, sortable: true, grow: 1.4 },
  ];

  // ---------- sorting ----------
  const onSort = (sortedColumn, sortDirection) => {
    sortingInProgressRef.current = true;
    const columnDirection = sortDirection ? sortDirection.toUpperCase() : null;
    setDefaultPage(gridState.pageNumber);
    setGridState((prev) => ({
      ...prev,
      orderBy: columnDirection !== null ? `${sortedColumn} ${columnDirection}` : "",
    }));
  };

  // ---------- effects ----------
  useEffect(() => {
    dispatch(dashboardActions.getDashboardStats());
    dispatch(recruiterActions.getAllRecruiterList());
  }, [dispatch]);

  const handleRecruiterGraph = (id) => {
    if (id) dispatch(dashboardActions.getDashboardRecruiterStats(id));
  };

  // ---------- build chart data from API and detect "empty" ----------
  const recruiterChart = useMemo(() => {
    const arr = (recruiterStats || [])
      .slice()
      .sort((a, b) => a.year - b.year || a.monthNumber - b.monthNumber)
      .map((r) => ({
        month: r.month,
        Accepted: r.accepted,
        Rejected: r.rejected,
        Completed: r.completedProjects,
      }));

    const hasData = arr.some((d) => d.Accepted || d.Rejected || d.Completed);
    return { data: arr, hasData };
  }, [recruiterStats]);

  if (!stats) return <div className="p-4">Loading dashboard…</div>;

  return (
    <div className="container-fluid home-scroll">
      {/* ---------- Top 4 cards ---------- */}
      <div className="row">
        {/* CARD 1 */}
        {(() => {
          const s = safeStat(stats, 0);
          const cls = getTrendClass(s.percentage);
          const icon = getTrendIcon(s.percentage);
          return (
            <div className="col-xl-3 col-md-6">
              <div className="card card-animate">
                <div className="card-body">
                  <div className="d-flex align-items-center">
                    <div className="flex-grow-1 overflow-hidden">
                      <p className="text-uppercase fw-medium text-muted text-truncate mb-0">
                        Accepted Candidates
                      </p>
                    </div>
                    <div className="flex-shrink-0">
                      <h5 className={`${cls} fs-14 mb-0`}>
                        <i className={`${icon} fs-13 align-middle`} /> {fmtPct(s.percentage)} %
                      </h5>
                    </div>
                  </div>
                  <div className="d-flex align-items-end justify-content-between mt-4">
                    <div>
                      <h4 className="fs-22 fw-semibold ff-secondary mb-4">{s.total}</h4>
                      <a href="#" className="text-decoration-underline">View All</a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })()}

        {/* CARD 2 */}
        {(() => {
          const s = safeStat(stats, 1);
          const cls = getTrendClass(s.percentage);
          const icon = getTrendIcon(s.percentage);
          return (
            <div className="col-xl-3 col-md-6">
              <div className="card card-animate">
                <div className="card-body">
                  <div className="d-flex align-items-center">
                    <div className="flex-grow-1 overflow-hidden">
                      <p className="text-uppercase fw-medium text-muted text-truncate mb-0">Rejected Candidates</p>
                    </div>
                    <div className="flex-shrink-0">
                      <h5 className={`${cls} fs-14 mb-0`}>
                        <i className={`${icon} fs-13 align-middle`} /> {fmtPct(s.percentage)} %
                      </h5>
                    </div>
                  </div>
                  <div className="d-flex align-items-end justify-content-between mt-4">
                    <div>
                      <h4 className="fs-22 fw-semibold ff-secondary mb-4">{s.total}</h4>
                      <a href="#" className="text-decoration-underline">View All</a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })()}

        {/* CARD 3 */}
        {(() => {
          const s = safeStat(stats, 2);
          const cls = getTrendClass(s.percentage);
          const icon = getTrendIcon(s.percentage);
          return (
            <div className="col-xl-3 col-md-6">
              <div className="card card-animate">
                <div className="card-body">
                  <div className="d-flex align-items-center">
                    <div className="flex-grow-1 overflow-hidden">
                      <p className="text-uppercase fw-medium text-muted text-truncate mb-0">Companies</p>
                    </div>
                    <div className="flex-shrink-0">
                      <h5 className={`${cls} fs-14 mb-0`}>
                        <i className={`${icon} fs-13 align-middle`} /> {fmtPct(s.percentage)} %
                      </h5>
                    </div>
                  </div>
                  <div className="d-flex align-items-end justify-content-between mt-4">
                    <div>
                      <h4 className="fs-22 fw-semibold ff-secondary mb-4">{s.total}</h4>
                      <a href="#" className="text-decoration-underline">See details</a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })()}

        {/* CARD 4 */}
        {(() => {
          const s = safeStat(stats, 3);
          const cls = getTrendClass(s.percentage);
          const icon = getTrendIcon(s.percentage);
          return (
            <div className="col-xl-3 col-md-6">
              <div className="card card-animate">
                <div className="card-body">
                  <div className="d-flex align-items-center">
                    <div className="flex-grow-1 overflow-hidden">
                      <p className="text-uppercase fw-medium text-muted text-truncate mb-0">Registered Candidates</p>
                    </div>
                    <div className="flex-shrink-0">
                      <h5 className={`${cls} fs-14 mb-0`}>
                        <i className={`${icon} fs-13 align-middle`} /> {fmtPct(s.percentage)} %
                      </h5>
                    </div>
                  </div>
                  <div className="d-flex align-items-end justify-content-between mt-4">
                    <div>
                      <h4 className="fs-22 fw-semibold ff-secondary mb-4">{s.total}</h4>
                      <a href="#" className="text-decoration-underline">View All Candidates</a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })()}
      </div>

      {/* ---------- Recruiter Statistics (Dynamic) ---------- */}
      <div className="row mt-4">
        <div className="col-xl-12">
          <div className="p-4 border rounded shadow bg-white">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5>Recruiter Statistics</h5>
              <select
                className="form-select w-auto"
                onChange={(e) => handleRecruiterGraph(e.target.value)}
              >
                <option value="">Select Recruiter</option>
                {recruiterList.map((rec) => (
                  <option key={rec.id} value={rec.id}>
                    {rec.name}
                  </option>
                ))}
              </select>
            </div>

            {recruiterStatsLoading ? (
              <div className="text-muted">Loading recruiter stats…</div>
            ) : recruiterChart.hasData ? (
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={recruiterChart.data} barGap={10}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Legend
                    payload={[
                      { value: "Accepted Candidates", type: "square", color: "#4F46E5", id: "Accepted" },
                      { value: "Completed Projects",  type: "square", color: "#10B981", id: "Completed" },
                      { value: "Rejected Candidates", type: "square", color: "#C4B5FD", id: "Rejected" },
                    ]}
                  />
                  {/* dataKey names MUST match the mapped keys above */}
                  <Bar dataKey="Accepted"  name="Accepted Candidates"  fill="#4F46E5" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="Completed" name="Completed Projects"   fill="#10B981" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="Rejected"  name="Rejected Candidates"  fill="#C4B5FD" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <EmptyChart
                onAction={() => {
                  const selects = document.querySelectorAll("select.form-select");
                  if (selects && selects.length) selects[0].focus();
                }}
                title="No recruiter activity in the last 6 months"
              />
            )}
          </div>
        </div>
      </div>

      {/* ---------- Recruiters Table ---------- */}
      {/* <div className="row mt-4 mb-4">
        <div className="col-12">
          <ActionsDataTable
            columns={columns}
            filterColumns={[]}
            data={recruiterList}
            pagination={true}
            highlightOnHover
            striped
            responsive
            persistTableHead
            selectableRows
            selectableRowsHighlight
            selectableRowsVisibleOnly
            paginationServer={false}
            onSort={onSort}
            gridState={gridState}
            paginationTotalRows={recruiterList.length}
            paginationDefaultPage={defaultPage}
            paginationResetDefaultPage
            allowToolbar={false}
            allowSelection={false}
            setGridState={setGridState}
            sortServer={true}
          />
        </div>
      </div> */}
    </div>
  );
};

export default Dashboard;
